import { api } from '@/lib/api-client';
import type { ParsedSkillMeta, SkillDetail } from './types';

export interface ListSkillsParams {
  search?: string;
  tags?: string[];
  page?: number;
  pageSize?: number;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listSkillsApi(params: ListSkillsParams): Promise<Paginated<SkillDetail>> {
  const search = params.search?.trim();
  const { data } = await api.get<Paginated<SkillDetail>>('/skills', {
    params: {
      ...(search ? { search } : {}),
      ...(params.tags?.length ? { tags: params.tags.join(',') } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    },
  });
  return data;
}

export async function getSkillApi(id: string): Promise<SkillDetail> {
  const { data } = await api.get<{ skill: SkillDetail }>(`/skills/${id}`);
  return data.skill;
}

export async function parseSkillZipApi(file: File): Promise<ParsedSkillMeta> {
  const form = new FormData();
  form.append('zip', file);
  const { data } = await api.post<ParsedSkillMeta>('/skills/parse-zip', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export interface CreateSkillInput {
  zip: File;
  name: string;
  description: string;
  howToUse: string;
  keyPoints: string;
  tags: string[];
}

export async function createSkillApi(input: CreateSkillInput): Promise<SkillDetail> {
  const form = new FormData();
  form.append('zip', input.zip);
  form.append('name', input.name);
  form.append('description', input.description);
  form.append('howToUse', input.howToUse);
  form.append('keyPoints', input.keyPoints);
  form.append('tags', JSON.stringify(input.tags));
  const { data } = await api.post<{ skill: SkillDetail }>('/skills', form);
  return data.skill;
}

export interface UpdateSkillInput {
  id: string;
  zip?: File | null;
  name?: string;
  description?: string;
  howToUse?: string;
  keyPoints?: string;
  tags?: string[];
}

export async function updateSkillApi(input: UpdateSkillInput): Promise<SkillDetail> {
  const form = new FormData();
  if (input.zip) form.append('zip', input.zip);
  if (input.name !== undefined) form.append('name', input.name);
  if (input.description !== undefined) form.append('description', input.description);
  if (input.howToUse !== undefined) form.append('howToUse', input.howToUse);
  if (input.keyPoints !== undefined) form.append('keyPoints', input.keyPoints);
  if (input.tags !== undefined) form.append('tags', JSON.stringify(input.tags));
  const { data } = await api.put<{ skill: SkillDetail }>(`/skills/${input.id}`, form);
  return data.skill;
}

export async function deleteSkillApi(id: string): Promise<void> {
  await api.delete(`/skills/${id}`);
}

export function getSkillDownloadUrl(id: string): string {
  const base = (api.defaults.baseURL ?? '/api/v1').replace(/\/$/, '');
  return `${base}/skills/${id}/download`;
}

export async function downloadSkillApi(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await api.get<Blob>(`/skills/${id}/download`, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'] ?? '';
  // RFC 5987 / 6266: filename="..." (sem aspas se sem espaços, mas o backend sempre manda com)
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] ?? `skill-${id}.zip`;
  return { blob: res.data, filename };
}

export async function listTagsApi(prefix?: string): Promise<{ id: string; name: string }[]> {
  const { data } = await api.get<{ items: { id: string; name: string }[] }>('/tags', {
    params: { prefix },
  });
  return data.items;
}
